import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout, { CATEGORIAS, puedeVer } from '../components/Layout';
import api from '../api';

const CATS_LABEL = { operaciones: 'OPS', catalogos: 'CAT', finanzas: 'FIN', admin: 'ADM' };
const CATS_ICON  = { operaciones: '🚛', catalogos: '📋', finanzas: '💰', admin: '⚙️' };

const Dashboard = ({ usuario, onLogout }) => {
  const navigate = useNavigate();
  const [categoriaActiva, setCategoriaActiva] = useState(null);
  const [stats, setStats] = useState({ viajesActivos: 0, viajesHoy: 0, excedentesPendientes: 0 });
  const tabsRef = useRef(null);
  const rol = usuario?.rol || 'conductor';

  // Categorías visibles = las que tienen al menos un módulo que el usuario puede ver (según su matriz)
  const categoriasFiltradas = CATEGORIAS
    .map(c => ({ ...c, modulos: c.modulos.filter(m => puedeVer(usuario, m.path)) }))
    .filter(c => c.modulos.length > 0);

  useEffect(() => {
    if (categoriasFiltradas.length > 0) setCategoriaActiva(categoriasFiltradas[0].id);
    cargarStats();
  }, []);

  useEffect(() => {
    if (!categoriaActiva || !tabsRef.current) return;
    const tabActivo = tabsRef.current.querySelector('[data-activo="true"]');
    if (tabActivo) {
      tabActivo.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [categoriaActiva]);

  const cargarStats = async () => {
    try {
      const res = await api.get('/viajes');
      const viajes = res.data || [];
      const hoy = new Date().toDateString();
      let excedentesPendientes = 0;
      if (puedeVer(usuario, '/excedentes')) {
        try {
          const excRes = await api.get('/excedentes');
          excedentesPendientes = (excRes.data || []).filter(e => e.estado === 'pendiente').length;
        } catch {}
      }
      setStats({
        viajesActivos: viajes.filter(v => v.estado === 'en_curso').length,
        viajesHoy: viajes.filter(v => new Date(v.creado_en).toDateString() === hoy).length,
        excedentesPendientes,
      });
    } catch {}
  };

  const catActual = categoriasFiltradas.find(c => c.id === categoriaActiva);
  const modulosFiltrados = catActual?.modulos || [];
  const ROLES_ES = { admin: 'Administrador', conductor: 'Conductor', despachador: 'Despachador', finanzas: 'Finanzas' };

  return (
    <Layout usuario={usuario} onLogout={onLogout} showDashboard={true}>
      {/* Saludo */}
      <div style={S.greeting}>
        <div style={S.greetingText}>
          <div style={S.greetingNombre}>Hola, {usuario?.nombre?.split(' ')[0]} 👋</div>
          <div style={S.greetingRol}>{ROLES_ES[rol]}</div>
        </div>
      </div>

      {/* Stats cards */}
      <div style={S.statsRow}>
        <div style={S.statCard} onClick={() => navigate('/viajes')}>
          <div style={S.statIcon}>🚛</div>
          <div style={S.statNum}>{stats.viajesActivos}</div>
          <div style={S.statLabel}>En Curso</div>
        </div>
        <div style={S.statCard} onClick={() => navigate('/viajes')}>
          <div style={S.statIcon}>📅</div>
          <div style={S.statNum}>{stats.viajesHoy}</div>
          <div style={S.statLabel}>Hoy</div>
        </div>
        {puedeVer(usuario, '/excedentes') && (
          <div style={{ ...S.statCard, borderColor: '#CC0000' }} onClick={() => navigate('/excedentes')}>
            <div style={S.statIcon}>💰</div>
            <div style={{ ...S.statNum, color: '#CC0000' }}>{stats.excedentesPendientes}</div>
            <div style={S.statLabel}>Excedentes</div>
          </div>
        )}
      </div>

      {/* Tabs categorías — Opción A: ícono + abreviatura */}
      <div style={S.tabsRow} ref={tabsRef}>
        {categoriasFiltradas.map(cat => {
          const activo = categoriaActiva === cat.id;
          const count = cat.modulos.length;
          return (
            <button
              key={cat.id}
              data-activo={activo ? 'true' : 'false'}
              style={{ ...S.tabBtn, ...(activo ? S.tabBtnActive : {}) }}
              onClick={() => setCategoriaActiva(cat.id)}
            >
              <span style={S.tabIcon}>{CATS_ICON[cat.id]}</span>
              <span style={S.tabLabel}>{CATS_LABEL[cat.id]}</span>
              <span style={{ ...S.tabCount, ...(activo ? S.tabCountActive : {}) }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Grid módulos */}
      <div style={S.modulosGrid}>
        {modulosFiltrados.map(mod => (
          <button key={mod.path} style={S.moduloCard} onClick={() => navigate(mod.path)}>
            <div style={S.moduloIcon}>{mod.icon}</div>
            <div style={S.moduloLabel}>{mod.label}</div>
          </button>
        ))}
      </div>
    </Layout>
  );
};

const S = {
  greeting: { background: '#1a1a1a', margin: '-16px -16px 16px', padding: '20px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  greetingText: {},
  greetingNombre: { color: '#fff', fontSize: '20px', fontWeight: '700', marginBottom: '4px' },
  greetingRol: { color: '#CC0000', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  statsRow: { display: 'flex', gap: '10px', marginBottom: '20px' },
  statCard: { flex: 1, background: '#fff', borderRadius: '12px', padding: '14px 10px', textAlign: 'center', border: '1.5px solid #e0e0e0', boxShadow: '0 2px 6px rgba(0,0,0,0.05)', cursor: 'pointer' },
  statIcon: { fontSize: '24px', marginBottom: '6px' },
  statNum: { fontSize: '26px', fontWeight: '800', color: '#1a1a1a', lineHeight: 1 },
  statLabel: { fontSize: '11px', color: '#888', marginTop: '4px', fontWeight: '500' },
  tabsRow: { display: 'flex', gap: '8px', marginBottom: '16px' },
  tabBtn: { flex: 1, padding: '8px 4px', borderRadius: '14px', border: '1.5px solid #e0e0e0', background: '#fff', color: '#666', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' },
  tabBtnActive: { background: '#1a1a1a', borderColor: '#1a1a1a', color: '#fff' },
  tabIcon: { fontSize: '18px', lineHeight: 1 },
  tabLabel: { fontSize: '11px', fontWeight: '700', lineHeight: 1 },
  tabCount: { background: '#f0f0f0', color: '#888', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: '700' },
  tabCountActive: { background: '#CC0000', color: '#fff' },
  modulosGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
  moduloCard: { background: '#fff', borderRadius: '14px', padding: '20px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', border: '1.5px solid #e0e0e0', boxShadow: '0 2px 6px rgba(0,0,0,0.05)', cursor: 'pointer', transition: 'all 0.15s' },
  moduloIcon: { fontSize: '36px', lineHeight: 1 },
  moduloLabel: { fontSize: '12px', fontWeight: '600', color: '#1a1a1a', textAlign: 'center', lineHeight: 1.3 },
};

export default Dashboard;
